// formData.ts

import * as Yup from "yup";

import { applyDocumentMask, determineDocumentType, validateCNPJ, validateCPF } from "@/helpers/document-helpers";
import { removeNonIntegersFromString } from "@/helpers/string-helpers";
import ICustomer from "@/interfaces/customer.interfaces";
import { ActivationFormData, CompanyType } from "@/interfaces/talk2pay.iterfaces";
import { ActivationMessages } from "@/messages/platforms/talk2PayConnection/activationMessages";
import { DocumentType } from "@/types/document.types";

export const buildInitialData = (customer: ICustomer | null): ActivationFormData => {
  const federalTaxId = customer?.federalTaxId || "";

  let formattedDocument = federalTaxId;
  if (federalTaxId) {
    const digitsOnly = removeNonIntegersFromString(federalTaxId);
    const mask = applyDocumentMask(digitsOnly);

    if (mask && digitsOnly) {
      let result = "";
      let numberIndex = 0;

      for (let i = 0; i < mask.length && numberIndex < digitsOnly.length; i++) {
        if (mask[i] === "9") {
          result += digitsOnly[numberIndex];
          numberIndex++;
        } else {
          result += mask[i];
        }
      }
      formattedDocument = result;
    }
  }

  let mobilePhone = customer?.phone1 || "";
  if (mobilePhone) {
    const phoneDigits = removeNonIntegersFromString(mobilePhone);
    if (phoneDigits.startsWith("55") && phoneDigits.length > 11) {
      mobilePhone = phoneDigits.slice(2);
    } else {
      mobilePhone = phoneDigits;
    }
  }

  return {
    name: customer?.name || "",
    email: customer?.email || "",
    document: formattedDocument,
    mobilePhone: mobilePhone,
    birthDate: customer?.birthDate || null,
    companyType: CompanyType.MEI,
    incomeValue: 0,
    address: customer?.address || "",
    addressNumber: customer?.addressNumber || "",
    complement: customer?.addressComp || "",
    province: customer?.district || "",
    zipCode: customer?.zipCode || "",
  };
};

export const validationSchema = (messages: ActivationMessages) =>
  Yup.object().shape({
    document: Yup.string()
      .required(messages.errorRequiredDocument)
      .test("valid-document", messages.errorInvalidDocument, (value) => {
        const cleanedValue = removeNonIntegersFromString(value || "");
        const isCPF = cleanedValue.length === 11;
        const isCNPJ = cleanedValue.length === 14;

        if (isCPF) {
          return validateCPF(cleanedValue);
        } else if (isCNPJ) {
          return validateCNPJ(cleanedValue);
        }

        return false;
      }),
    name: Yup.string().required(messages.errorRequiredName),
    email: Yup.string().email(messages.errorInvalidEmail).required(messages.errorRequiredEmail),
    mobilePhone: Yup.string().required(messages.errorRequiredMobilePhone),
    birthDate: Yup.string()
      .when("document", {
        is: (value: string) => {
          const cleanedValue = removeNonIntegersFromString(value || "");
          return cleanedValue.length === 11;
        },
        then: (schema) => schema.required(messages.errorRequiredBirthDate),
        otherwise: (schema) => schema.nullable(),
      }),
    companyType: Yup.string()
      .required(messages.errorRequiredCompanyType)
      .oneOf(Object.values(CompanyType), messages.errorInvalidCompanyType),
    incomeValue: Yup.number()
      .required(messages.errorRequiredIncomeValue)
      .moreThan(0, messages.errorInvalidIncomeValue),
    address: Yup.string().required(messages.errorRequiredAddress),
    addressNumber: Yup.string().required(messages.errorRequiredAddressNumber),
    complement: Yup.string().optional(),
    province: Yup.string().required(messages.errorRequiredProvince),
    zipCode: Yup.string().required(messages.errorRequiredCEP),
  });

export function translateCompanyType(
  companyType: CompanyType,
  messages: ActivationMessages
): { value: string; label: string } {
  switch (companyType) {
    case CompanyType.MEI:
      return { value: companyType, label: messages.companyTypeMEILabel };
    case CompanyType.LIMITED:
      return { value: companyType, label: messages.companyTypeLimitedLabel };
    case CompanyType.INDIVIDUAL:
      return { value: companyType, label: messages.companyTypeIndividualLabel };
    default:
      return { value: companyType, label: "" };
  }
}

export const normalizeActivationFormData = (formData: ActivationFormData): ActivationFormData => {
  const cleanMobilePhone = removeNonIntegersFromString(formData.mobilePhone);

  const mobilePhoneWithoutDDI = cleanMobilePhone.startsWith("55")
    ? cleanMobilePhone.slice(2)
    : cleanMobilePhone;

  return {
    name: formData.name,
    email: formData.email,
    document: removeNonIntegersFromString(formData.document),
    mobilePhone: mobilePhoneWithoutDDI,
    birthDate: formData.birthDate || null,
    companyType: formData.companyType,
    incomeValue: parseFloat(formData.incomeValue?.toString() || "0.0"),
    address: formData.address,
    addressNumber: formData.addressNumber,
    complement: formData.complement || "",
    province: formData.province,
    zipCode: removeNonIntegersFromString(formData.zipCode),
  };
};
